import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Divider,
  Chip,
  Avatar,
  AvatarGroup,
  LinearProgress,
  Tooltip,
} from '@mui/material';
import {
  Clock,
  CheckCircle,
  AlertCircle,
  Calendar,
  TrendingUp,
  Target,
} from 'lucide-react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

import { Project, Task, TaskStatus, TaskPriority, Milestone } from '@/types';
import { formatPercent } from '@/shared/utils/formatPercentage';

dayjs.extend(relativeTime);

interface ProjectOverviewTabProps {
  project: Project;
  tasks: Task[];
  stats: {
    totalTasks: number;
    completedTasks: number;
    progress: number;
    totalTime: number;
    estimatedTime: number;
    taskEstimatedTime?: number;
    projectEstimatedTime?: number;
  };
}

/**
 * ProjectOverviewTab
 * Project dashboard with key metrics and recent activity
 */
const ProjectOverviewTab: React.FC<ProjectOverviewTabProps> = ({
  project,
  tasks,
  stats,
}) => {
  // Calculate task breakdown by status
  const getStatusBreakdown = () => {
    const breakdown: Record<TaskStatus, number> = {
      [TaskStatus.NOT_STARTED]: 0,
      [TaskStatus.IN_PROGRESS]: 0,
      [TaskStatus.BLOCKED]: 0,
      [TaskStatus.COMPLETED]: 0,
      [TaskStatus.CANCELLED]: 0,
    };

    tasks.forEach((task) => {
      if (breakdown[task.status] !== undefined) {
        breakdown[task.status]++;
      }
    });

    return breakdown;
  };

  // Calculate task breakdown by priority
  const getPriorityBreakdown = () => {
    const breakdown: Record<TaskPriority, number> = {
      [TaskPriority.P4]: 0, // Low
      [TaskPriority.P3]: 0, // Medium
      [TaskPriority.P2]: 0, // High
      [TaskPriority.P1]: 0, // Critical
    };

    tasks.forEach((task) => {
      if (breakdown[task.priority] !== undefined) {
        breakdown[task.priority]++;
      }
    });

    return breakdown;
  };

  // Get overdue tasks
  const getOverdueTasks = () => {
    return tasks.filter(
      (task) =>
        task.status !== TaskStatus.COMPLETED &&
        dayjs(task.dueDate).isBefore(dayjs())
    );
  };

  // Get upcoming deadlines
  const getUpcomingDeadlines = () => {
    return tasks
      .filter(
        (task) =>
          task.status !== TaskStatus.COMPLETED &&
          dayjs(task.dueDate).isAfter(dayjs()) &&
          dayjs(task.dueDate).isBefore(dayjs().add(7, 'day'))
      )
      .sort((a, b) => dayjs(a.dueDate).diff(dayjs(b.dueDate)));
  };

  // Get milestone stats
  const getMilestoneStats = () => {
    const milestones = project.milestones || [];
    const total = milestones.length;
    const completed = milestones.filter((m) => m.isCompleted).length;
    const overdue = milestones.filter(
      (m) => !m.isCompleted && dayjs(m.dueDate).isBefore(dayjs())
    ).length;
    const upcoming = milestones.filter(
      (m) => !m.isCompleted && 
             dayjs(m.dueDate).isAfter(dayjs()) && 
             dayjs(m.dueDate).isBefore(dayjs().add(14, 'day'))
    ).length;
    
    // Get next upcoming milestone
    const nextMilestone = milestones
      .filter((m) => !m.isCompleted)
      .sort((a, b) => dayjs(a.dueDate).diff(dayjs(b.dueDate)))[0] || null;
    
    return { total, completed, overdue, upcoming, nextMilestone };
  };

  const milestoneStats = getMilestoneStats();

  const statusBreakdown = getStatusBreakdown();
  const priorityBreakdown = getPriorityBreakdown();
  const overdueTasks = getOverdueTasks();
  const upcomingDeadlines = getUpcomingDeadlines();

  const statusColors: Record<string, string> = {
    not_started: '#64748b',
    in_progress: '#2563eb',
    on_hold: '#f59e0b',
    blocked: '#ef4444',
    completed: '#16a34a',
    cancelled: '#94a3b8',
  };

  const priorityColors: Record<string, string> = {
    low: '#94a3b8',
    medium: '#2563eb',
    high: '#f59e0b',
    urgent: '#ef4444',
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Project Info */}
      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            About this Project
          </Typography>
          <Typography variant="body2">
            {project.description || 'No description provided'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 3, mt: 2 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Start Date
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {project.startDate && dayjs(project.startDate).isValid()
                  ? dayjs(project.startDate).format('MMM D, YYYY')
                  : '—'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                End Date
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {project.endDate && dayjs(project.endDate).isValid()
                  ? dayjs(project.endDate).format('MMM D, YYYY')
                  : '—'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Duration
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {project.startDate && project.endDate && dayjs(project.startDate).isValid() && dayjs(project.endDate).isValid()
                  ? `${dayjs(project.endDate).diff(dayjs(project.startDate), 'day')} days`
                  : '—'}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Status & Priority Breakdown */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
        {/* Status Breakdown */}
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle2" gutterBottom>
              Task Status Distribution
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2 }}>
              {Object.entries(statusBreakdown)
                .filter(([_, count]) => count > 0)
                .map(([status, count]) => (
                  <Box key={status}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        mb: 0.5,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ textTransform: 'capitalize' }}
                      >
                        {status.replace('_', ' ')}
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {count}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={stats.totalTasks > 0 ? (count / stats.totalTasks) * 100 : 0}
                      sx={{
                        height: 6,
                        borderRadius: 1,
                        bgcolor: 'grey.100',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: statusColors[status],
                        },
                      }}
                    />
                  </Box>
                ))}
            </Box>
          </CardContent>
        </Card>

        {/* Priority Breakdown */}
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle2" gutterBottom>
              Task Priority Distribution
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2 }}>
              {Object.entries(priorityBreakdown)
                .filter(([_, count]) => count > 0)
                .map(([priority, count]) => (
                  <Box key={priority}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        mb: 0.5,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ textTransform: 'capitalize' }}
                      >
                        {priority}
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {count}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={stats.totalTasks > 0 ? (count / stats.totalTasks) * 100 : 0}
                      sx={{
                        height: 6,
                        borderRadius: 1,
                        bgcolor: 'grey.100',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: priorityColors[priority],
                        },
                      }}
                    />
                  </Box>
                ))}
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Milestone Summary */}
      {milestoneStats.total > 0 && (
        <Card variant="outlined" sx={{ borderColor: milestoneStats.overdue > 0 ? 'warning.main' : 'divider' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Target size={18} style={{ color: '#8b5cf6' }} />
              <Typography variant="subtitle2">
                Milestones Overview
              </Typography>
            </Box>
            
            {/* Milestone Stats */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2, mb: 3 }}>
              <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="h5" fontWeight={700} color="primary.main">
                  {milestoneStats.total}
                </Typography>
                <Typography variant="caption" color="text.secondary">Total</Typography>
              </Box>
              <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: '#dcfce7', borderRadius: 1 }}>
                <Typography variant="h5" fontWeight={700} sx={{ color: '#166534' }}>
                  {milestoneStats.completed}
                </Typography>
                <Typography variant="caption" sx={{ color: '#166534' }}>Completed</Typography>
              </Box>
              <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: '#fef3c7', borderRadius: 1 }}>
                <Typography variant="h5" fontWeight={700} sx={{ color: '#92400e' }}>
                  {milestoneStats.upcoming}
                </Typography>
                <Typography variant="caption" sx={{ color: '#92400e' }}>Upcoming</Typography>
              </Box>
              <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: milestoneStats.overdue > 0 ? '#fee2e2' : 'grey.50', borderRadius: 1 }}>
                <Typography variant="h5" fontWeight={700} sx={{ color: milestoneStats.overdue > 0 ? '#991b1b' : 'text.secondary' }}>
                  {milestoneStats.overdue}
                </Typography>
                <Typography variant="caption" sx={{ color: milestoneStats.overdue > 0 ? '#991b1b' : 'text.secondary' }}>Overdue</Typography>
              </Box>
            </Box>

            {/* Progress Bar */}
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Progress
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {formatPercent(milestoneStats.total > 0 ? Math.round((milestoneStats.completed / milestoneStats.total) * 100) : 0)}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={milestoneStats.total > 0 ? (milestoneStats.completed / milestoneStats.total) * 100 : 0}
                sx={{
                  height: 8,
                  borderRadius: 1,
                  bgcolor: 'grey.200',
                  '& .MuiLinearProgress-bar': {
                    bgcolor: '#8b5cf6',
                    borderRadius: 1,
                  },
                }}
              />
            </Box>

            {/* Next Milestone */}
            {milestoneStats.nextMilestone && (
              <Box sx={{ p: 2, bgcolor: '#f5f3ff', borderRadius: 1, border: '1px solid #ddd6fe' }}>
                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                  Next Milestone
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="body1" fontWeight={600}>
                      {milestoneStats.nextMilestone.name}
                    </Typography>
                    {milestoneStats.nextMilestone.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {milestoneStats.nextMilestone.description}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="body2" fontWeight={500}>
                      {dayjs(milestoneStats.nextMilestone.dueDate).format('MMM D, YYYY')}
                    </Typography>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: dayjs(milestoneStats.nextMilestone.dueDate).isBefore(dayjs()) ? 'error.main' : 'text.secondary'
                      }}
                    >
                      {dayjs(milestoneStats.nextMilestone.dueDate).fromNow()}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Overdue & Upcoming */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
        {/* Overdue Tasks */}
        <Card variant="outlined" sx={{ borderColor: overdueTasks.length > 0 ? 'error.main' : 'divider' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <AlertCircle size={18} style={{ color: '#ef4444' }} />
              <Typography variant="subtitle2">
                Overdue Tasks ({overdueTasks.length})
              </Typography>
            </Box>
            {overdueTasks.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <CheckCircle size={32} style={{ color: '#16a34a', marginBottom: 8 }} />
                <Typography variant="body2" color="text.secondary">
                  No overdue tasks!
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {overdueTasks.slice(0, 5).map((task) => (
                  <Box
                    key={task.id}
                    sx={{
                      p: 1.5,
                      bgcolor: 'error.50',
                      borderRadius: 1,
                      borderLeft: '3px solid',
                      borderColor: 'error.main',
                    }}
                  >
                    <Typography variant="body2" fontWeight={500}>
                      {task.title}
                    </Typography>
                    <Typography variant="caption" color="error.main">
                      Due: {dayjs(task.dueDate).format('MMM D')} (
                      {dayjs(task.dueDate).fromNow()})
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Upcoming Deadlines */}
        <Card variant="outlined">
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Calendar size={18} style={{ color: '#2563eb' }} />
              <Typography variant="subtitle2">
                Upcoming Deadlines ({upcomingDeadlines.length})
              </Typography>
            </Box>
            {upcomingDeadlines.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <Calendar size={32} style={{ color: '#94a3b8', marginBottom: 8 }} />
                <Typography variant="body2" color="text.secondary">
                  No upcoming deadlines this week
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {upcomingDeadlines.slice(0, 5).map((task) => (
                  <Box
                    key={task.id}
                    sx={{
                      p: 1.5,
                      bgcolor: 'grey.50',
                      borderRadius: 1,
                      borderLeft: '3px solid',
                      borderColor: 'primary.main',
                    }}
                  >
                    <Typography variant="body2" fontWeight={500}>
                      {task.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Due: {dayjs(task.dueDate).format('MMM D, YYYY')}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* Time Tracking Summary */}
      <Card variant="outlined">
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Clock size={18} style={{ color: '#f59e0b' }} />
            <Typography variant="subtitle2">Time Tracking Summary</Typography>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 3 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Total Logged
              </Typography>
              <Typography variant="h6" fontWeight={600}>
                {formatDuration(stats.totalTime)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Estimated
              </Typography>
              <Typography variant="h6" fontWeight={600}>
                {formatDuration(stats.estimatedTime)}
              </Typography>
              {stats.projectEstimatedTime != null && stats.projectEstimatedTime > 0 && (
                <Typography variant="caption" color="text.secondary" display="block">
                  project budget
                  {stats.taskEstimatedTime != null && stats.taskEstimatedTime > 0
                    ? ` · ${formatDuration(stats.taskEstimatedTime)} in tasks`
                    : ''}
                </Typography>
              )}
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Variance
              </Typography>
              <Typography
                variant="h6"
                fontWeight={600}
                sx={{
                  color:
                    stats.totalTime > stats.estimatedTime
                      ? 'error.main'
                      : 'success.main',
                }}
              >
                {stats.totalTime > stats.estimatedTime ? '+' : ''}
                {formatDuration(Math.abs(stats.totalTime - stats.estimatedTime))}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Efficiency
              </Typography>
              <Typography
                variant="h6"
                fontWeight={600}
                sx={{
                  color:
                    stats.estimatedTime > 0 &&
                    stats.totalTime / stats.estimatedTime <= 1
                      ? 'success.main'
                      : 'warning.main',
                }}
              >
                {stats.estimatedTime > 0
                  ? formatPercent(Math.round((stats.totalTime / stats.estimatedTime) * 100))
                  : 'N/A'}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ProjectOverviewTab;
